import React from "react";
import {
  Table,
  Modal,
  Empty,
  Tag,
  Button,
  Tooltip,
  Divider,
  Icon,
  notification,
} from "antd";
import axios from "axios";
import { Constant } from "../../../config";
import "./MemberCreditWalletModal.css";

// Import modular components
import {
  // Utils
  statusConfig,
  formatDate,
  getCurrencyDisplay,
  // Layout Components
  WalletSidebar,
  WalletSummaryCards,
  // Modal Components
  EditCreditModal,
  AddCreditModal,
  TransferPackageModal,
  TransferToBalanceModal,
  ExtendExpiryModal,
  DeleteWalletModal,
  ActivityLogsModal,
  WalletDetailModal,
} from "./components/MemberCreditWallet";

class MemberCreditWalletModel extends React.Component {
  state = {
    wallets: {},
    member: {},
    groupedData: {},
    selectedType: null,
    selectedCountry: null,
    tableData: [],
    // CRUD Modal states
    editModalVisible: false,
    editRecord: null,
    editMode: "adjust",
    editCredit: null,
    editAdjustment: null,
    editNote: "",
    extendModalVisible: false,
    extendRecord: null,
    extendDate: null,
    extendNote: "",
    deleteModalVisible: false,
    deleteRecord: null,
    deleteNote: "",
    loading: false,
    // Add Credit Modal states
    addModalVisible: false,
    packages: [],
    packagesLoading: false,
    selectedPackageId: null,
    addNote: "",
    // Transfer Package Modal states
    transferModalVisible: false,
    transferRecord: null,
    transferPackageId: null,
    currentPackageInfo: null,
    transferNote: "",
    // Transfer to Balance Modal states
    transferToBalanceModalVisible: false,
    transferToBalanceRecord: null,
    transferToBalanceNote: "",
    transferToBalanceAmount: 0,
    // Activity Logs Modal states
    activityLogsModalVisible: false,
    activityLogsRecord: null,
    activityLogs: [],
    activityLogsLoading: false,
    expandedLogIds: [],
    activityFilterWallet: "all",
    activityFilterAction: "all",
    // Wallet Detail Modal from activity log
    walletDetailModalVisible: false,
    walletDetailData: null,
  };

  componentDidMount() {
    const { data } = this.props;
    const walletData = data.detail.wallet || {};
    const grouped = this.groupWalletsByType(walletData);
    const firstType = Object.keys(grouped)[0];
    const firstCountry = firstType ? Object.keys(grouped[firstType])[0] : null;

    this.setState(
      {
        wallets: walletData,
        member: data.detail,
        groupedData: grouped,
        selectedType: firstType || null,
        selectedCountry: firstCountry || null,
      },
      () => this.updateTableData()
    );
  }

  // ============ Data Processing ============
  groupWalletsByType = (walletData) => {
    const grouped = { sms: {}, viber: {}, whatsapp: {} };
    Object.entries(walletData).forEach(([country, wallets]) => {
      wallets.forEach((wallet) => {
        const type = wallet.type || "sms";
        if (!grouped[type]) grouped[type] = {};
        if (!grouped[type][country]) grouped[type][country] = [];
        grouped[type][country].push(wallet);
      });
    });
    Object.keys(grouped).forEach((type) => {
      if (Object.keys(grouped[type]).length === 0) delete grouped[type];
    });
    return grouped;
  };

  updateTableData = () => {
    const { groupedData, selectedType, selectedCountry } = this.state;
    if (selectedType && selectedCountry && groupedData[selectedType]) {
      this.setState({ tableData: groupedData[selectedType][selectedCountry] || [] });
    } else {
      this.setState({ tableData: [] });
    }
  };

  handleMenuSelect = (type, country) => {
    this.setState({ selectedType: type, selectedCountry: country }, () => this.updateTableData());
  };

  // ============ Helper Methods ============
  getTotalCredits = (type) => {
    const { groupedData } = this.state;
    if (!groupedData[type]) return { available: 0, purchased: 0, count: 0 };
    let available = 0, purchased = 0, count = 0;
    Object.values(groupedData[type]).forEach((wallets) => {
      wallets.forEach((w) => {
        available += w.credit_available || 0;
        purchased += w.credit_purchased || 0;
        count++;
      });
    });
    return { available, purchased, count };
  };

  getCountryCredits = (type, country) => {
    const { groupedData } = this.state;
    if (!groupedData[type] || !groupedData[type][country]) return { available: 0, count: 0 };
    let available = 0;
    const count = groupedData[type][country].length;
    groupedData[type][country].forEach((w) => { available += w.credit_available || 0; });
    return { available, count };
  };

  getStatusTag = (status) => {
    const config = statusConfig[status] || statusConfig.enabled;
    return (
      <Tag style={{ backgroundColor: config.bgColor, color: '#fff', border: 'none', fontWeight: 500 }}>
        {config.text}
      </Tag>
    );
  };

  updateLocalWalletData = (walletId, updates) => {
    const { wallets } = this.state;
    const newWallets = JSON.parse(JSON.stringify(wallets));
    let found = false;
    Object.keys(newWallets).forEach((country) => {
      newWallets[country] = newWallets[country].map((w) => {
        if (w.id === walletId) { found = true; return { ...w, ...updates }; }
        return w;
      });
      if (updates === null) {
        newWallets[country] = newWallets[country].filter(w => w.id !== walletId);
      }
    });
    if (found) {
      const grouped = this.groupWalletsByType(newWallets);
      this.setState({ wallets: newWallets, groupedData: grouped }, () => this.updateTableData());
    }
  };

  // ============ Edit Credit Modal ============
  openEditModal = (record) => {
    this.setState({
      editModalVisible: true, editRecord: record, editMode: "adjust",
      editCredit: null, editAdjustment: null, editNote: "",
    });
  };

  closeEditModal = () => {
    this.setState({
      editModalVisible: false, editRecord: null, editMode: "adjust",
      editCredit: null, editAdjustment: null, editNote: "",
    });
  };

  handleEditSubmit = async () => {
    const { editRecord, editMode, editCredit, editAdjustment, editNote } = this.state;
    if (!editRecord) return;
    this.setState({ loading: true });
    try {
      const url = Constant.MEMBER_CREDIT_WALLET_UPDATE_CREDIT.replace("[id]", editRecord.id);
      const requestData = editMode === "direct"
        ? { mode: "direct", credit: editCredit, note: editNote }
        : { mode: "adjust", adjustment: editAdjustment, note: editNote };
      const response = await axios({ url, method: "PATCH", headers: { Authorization: "Bearer " + localStorage.getItem(Constant.SessionKey) }, data: requestData });
      notification.success({ message: "Credit updated successfully" });
      this.closeEditModal();
      const apiData = response.data && response.data.data;
      const newCredit = (apiData && apiData.credit_available) || editCredit;
      this.updateLocalWalletData(editRecord.id, { credit_available: newCredit });
      // Notify parent to refresh member data
      if (this.props.onWalletUpdate) this.props.onWalletUpdate();
    } catch (error) {
      const errMsg = (error.response && error.response.data && error.response.data.error && error.response.data.error.description) || error.message;
      notification.error({ message: "Failed to update credit", description: errMsg });
    } finally {
      this.setState({ loading: false });
    }
  };

  // ============ Extend Expiry Modal ============
  openExtendModal = (record) => {
    this.setState({ extendModalVisible: true, extendRecord: record, extendDate: null, extendNote: "" });
  };

  closeExtendModal = () => {
    this.setState({ extendModalVisible: false, extendRecord: null, extendDate: null, extendNote: "" });
  };

  handleExtendSubmit = async () => {
    const { extendRecord, extendDate, extendNote } = this.state;
    if (!extendRecord || !extendDate) return;
    this.setState({ loading: true });
    try {
      const url = Constant.MEMBER_CREDIT_WALLET_EXTEND.replace("[id]", extendRecord.id);
      await axios({ url, method: "PATCH", headers: { Authorization: "Bearer " + localStorage.getItem(Constant.SessionKey) }, data: { expire_date: extendDate.format("YYYY-MM-DD"), note: extendNote } });
      notification.success({ message: "Expiry extended successfully" });
      this.closeExtendModal();
      this.updateLocalWalletData(extendRecord.id, { expire_date: { Valid: true, Time: extendDate.toISOString() } });
      // Notify parent to refresh member data
      if (this.props.onWalletUpdate) this.props.onWalletUpdate();
    } catch (error) {
      notification.error({ message: "Failed to extend expiry", description: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  // ============ Delete Wallet Modal ============
  openDeleteModal = (record) => {
    this.setState({ deleteModalVisible: true, deleteRecord: record, deleteNote: "" });
  };

  closeDeleteModal = () => {
    this.setState({ deleteModalVisible: false, deleteRecord: null, deleteNote: "" });
  };

  handleDeleteSubmit = async () => {
    const { deleteRecord, deleteNote } = this.state;
    if (!deleteRecord) return;
    this.setState({ loading: true });
    try {
      const url = Constant.MEMBER_CREDIT_WALLET_DELETE.replace("[id]", deleteRecord.id);
      await axios({ url, method: "DELETE", headers: { Authorization: "Bearer " + localStorage.getItem(Constant.SessionKey) }, data: { note: deleteNote } });
      notification.success({ message: "Wallet deleted successfully" });
      this.closeDeleteModal();
      this.updateLocalWalletData(deleteRecord.id, null);
      // Notify parent to refresh member data
      if (this.props.onWalletUpdate) this.props.onWalletUpdate();
    } catch (error) {
      notification.error({ message: "Failed to delete wallet", description: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  // ============ Add Credit Modal ============
  openAddModal = async () => {
    this.setState({ addModalVisible: true, packagesLoading: true, selectedPackageId: null, addNote: "" });
    try {
      const response = await axios({ url: Constant.PRODUCT_PACKAGES_TRIAL, method: "GET", headers: { Authorization: "Bearer " + localStorage.getItem(Constant.SessionKey) } });
      this.setState({ packages: response.data.data || [], packagesLoading: false });
    } catch (error) {
      notification.error({ message: "Failed to load packages" });
      this.setState({ packagesLoading: false });
    }
  };

  closeAddModal = () => {
    this.setState({ addModalVisible: false, packages: [], selectedPackageId: null, addNote: "" });
  };

  handleAddSubmit = async () => {
    const { member, selectedPackageId, packages, addNote } = this.state;
    const selectedPkg = packages.find((p) => p.ID === selectedPackageId);
    if (!selectedPackageId || !selectedPkg) { notification.error({ message: "Please select a package" }); return; }
    this.setState({ loading: true });
    try {
      const response = await axios({ url: Constant.MEMBER_CREDIT_WALLET_CREATE, method: "POST", headers: { Authorization: "Bearer " + localStorage.getItem(Constant.SessionKey) }, data: { member_id: member.id, product_id: selectedPackageId, note: addNote } });
      notification.success({ message: "Wallet created successfully" });
      this.closeAddModal();
      const data = response.data.data;
      const newWallet = { id: data.id, type: data.type, country: data.country, credit_available: data.credit, credit_purchased: data.credit, unit_price: data.unit_price, expire_date: { Valid: true, Time: data.expire_date + "T00:00:00Z" }, status: "enabled", package_name: data.package_name, package_plan: data.package_plan, created_date: { Valid: true, Time: new Date().toISOString() } };
      const { wallets } = this.state;
      const newWallets = JSON.parse(JSON.stringify(wallets));
      if (!newWallets[data.country]) newWallets[data.country] = [];
      newWallets[data.country].push(newWallet);
      const grouped = this.groupWalletsByType(newWallets);
      this.setState({ wallets: newWallets, groupedData: grouped }, () => this.updateTableData());
      // Notify parent to refresh member data
      if (this.props.onWalletUpdate) this.props.onWalletUpdate();
    } catch (error) {
      const errMsg = (error.response && error.response.data && error.response.data.error && error.response.data.error.description) || error.message;
      notification.error({ message: "Failed to create wallet", description: errMsg });
    } finally {
      this.setState({ loading: false });
    }
  };

  // ============ Transfer Package Modal ============
  openTransferModal = async (record) => {
    if (record.credit_available !== record.credit_purchased) {
      notification.error({ message: "Cannot Transfer", description: "You can only transfer packages with full credit (credit not used yet)." });
      return;
    }
    this.setState({ transferModalVisible: true, transferRecord: record, transferPackageId: null, transferNote: "", packagesLoading: true });
    try {
      const response = await axios({ url: Constant.PRODUCT_PACKAGES, method: "GET", headers: { Authorization: "Bearer " + localStorage.getItem(Constant.SessionKey) } });
      const allPackages = response.data.data || [];
      const currentPkg = allPackages.find(pkg => pkg.ID === record.product_id);
      const currentAmount = currentPkg ? currentPkg.Amount : null;
      const filteredPackages = allPackages.filter(pkg => pkg.Type === record.type && pkg.Amount === currentAmount && pkg.ID !== record.product_id);
      this.setState({ packages: filteredPackages, currentPackageInfo: currentPkg, packagesLoading: false });
    } catch (error) {
      notification.error({ message: "Failed to load packages" });
      this.setState({ packagesLoading: false });
    }
  };

  closeTransferModal = () => {
    this.setState({ transferModalVisible: false, transferRecord: null, transferPackageId: null, transferNote: "", packages: [] });
  };

  handleTransferSubmit = async () => {
    const { transferRecord, transferPackageId, transferNote, packages } = this.state;
    if (!transferRecord || !transferPackageId) { notification.error({ message: "Please select a package" }); return; }
    this.setState({ loading: true });
    try {
      const url = Constant.MEMBER_CREDIT_WALLET_TRANSFER.replace("[id]", transferRecord.id);
      const response = await axios({ url, method: "PATCH", headers: { Authorization: "Bearer " + localStorage.getItem(Constant.SessionKey) }, data: { new_product_id: transferPackageId, note: transferNote } });
      notification.success({ message: "Package transferred successfully" });
      this.closeTransferModal();
      const selectedPkg = packages.find(p => p.ID === transferPackageId);
      const apiData = response.data.data || {};
      const newCountry = apiData.country || transferRecord.country;
      const { wallets } = this.state;
      const newWallets = JSON.parse(JSON.stringify(wallets));
      if (newCountry !== transferRecord.country) {
        if (newWallets[transferRecord.country]) newWallets[transferRecord.country] = newWallets[transferRecord.country].filter(w => w.id !== transferRecord.id);
        if (!newWallets[newCountry]) newWallets[newCountry] = [];
        newWallets[newCountry].push({ ...transferRecord, product_id: transferPackageId, package_name: (selectedPkg && selectedPkg.Name) || "Transferred", country: newCountry, credit_available: apiData.credit_available || transferRecord.credit_available, credit_purchased: apiData.credit_purchased || transferRecord.credit_purchased, unit_price: apiData.unit_price || transferRecord.unit_price });
      } else {
        Object.keys(newWallets).forEach(country => { newWallets[country] = newWallets[country].map(w => w.id === transferRecord.id ? { ...w, product_id: transferPackageId, package_name: (selectedPkg && selectedPkg.Name) || "Transferred", country: newCountry, credit_available: apiData.credit_available || w.credit_available, credit_purchased: apiData.credit_purchased || w.credit_purchased, unit_price: apiData.unit_price || w.unit_price } : w); });
      }
      const grouped = this.groupWalletsByType(newWallets);
      this.setState({ wallets: newWallets, groupedData: grouped }, () => this.updateTableData());
      // Notify parent to refresh member data
      if (this.props.onWalletUpdate) this.props.onWalletUpdate();
    } catch (error) {
      const errMsg = (error.response && error.response.data && error.response.data.error && error.response.data.error.description) || error.message;
      notification.error({ message: "Failed to transfer package", description: errMsg });
    } finally {
      this.setState({ loading: false });
    }
  };

  // ============ Transfer to Balance Modal ============
  openTransferToBalanceModal = async (record) => {
    if (record.credit_available !== record.credit_purchased) {
      notification.error({ message: "Cannot Transfer", description: "You can only transfer packages with full credit (credit not used yet)." });
      return;
    }
    try {
      const url = Constant.PRODUCT_PACKAGE_BY_ID.replace("[id]", record.product_id);
      const response = await axios({ url, method: "GET", headers: { Authorization: "Bearer " + localStorage.getItem(Constant.SessionKey) } });
      const pkg = response.data.data;
      const balanceAmount = pkg ? pkg.Amount : (record.credit_available * record.unit_price);
      this.setState({ transferToBalanceModalVisible: true, transferToBalanceRecord: record, transferToBalanceNote: "", transferToBalanceAmount: balanceAmount });
    } catch (error) {
      notification.error({ message: "Failed to load package info" });
    }
  };

  closeTransferToBalanceModal = () => {
    this.setState({ transferToBalanceModalVisible: false, transferToBalanceRecord: null, transferToBalanceNote: "", transferToBalanceAmount: 0 });
  };

  handleTransferToBalanceSubmit = async () => {
    const { transferToBalanceRecord, transferToBalanceNote, member } = this.state;
    if (!transferToBalanceRecord) return;
    this.setState({ loading: true });
    try {
      const url = Constant.MEMBER_CREDIT_WALLET_TRANSFER_TO_BALANCE.replace("[id]", transferToBalanceRecord.id);
      const response = await axios({ url, method: "PATCH", headers: { Authorization: "Bearer " + localStorage.getItem(Constant.SessionKey) }, data: { note: transferToBalanceNote } });
      const apiData = response.data.data || {};
      notification.success({ message: "Transferred to Balance", description: "Added " + ((apiData.balance_added && apiData.balance_added.toFixed(2)) || 0) + " THB to balance. New balance: " + ((apiData.balance_after && apiData.balance_after.toFixed(2)) || 0) + " THB" });
      this.closeTransferToBalanceModal();
      const { wallets } = this.state;
      const newWallets = JSON.parse(JSON.stringify(wallets));
      Object.keys(newWallets).forEach(country => { newWallets[country] = newWallets[country].filter(w => w.id !== transferToBalanceRecord.id); });
      const newMember = { ...member, balance: apiData.balance_after || member.balance };
      const grouped = this.groupWalletsByType(newWallets);
      this.setState({ wallets: newWallets, groupedData: grouped, member: newMember }, () => this.updateTableData());
      // Notify parent to refresh member data
      if (this.props.onWalletUpdate) this.props.onWalletUpdate();
    } catch (error) {
      const errMsg = (error.response && error.response.data && error.response.data.error && error.response.data.error.description) || error.message;
      notification.error({ message: "Failed to transfer to balance", description: errMsg });
    } finally {
      this.setState({ loading: false });
    }
  };

  // ============ Activity Logs Modal ============
  openActivityLogsModal = () => {
    const { member } = this.state;
    this.setState({ activityLogsModalVisible: true, activityLogsRecord: member, activityLogs: [], activityLogsLoading: true, expandedLogIds: [] });
    this.fetchActivityLogs(member.id);
  };

  fetchActivityLogs = async (memberId) => {
    try {
      const url = Constant.MEMBER_WALLET_ALL_LOGS.replace("[member_id]", memberId);
      const { data } = await axios.get(url, { headers: { Authorization: "Bearer " + localStorage.getItem(Constant.SessionKey) } });
      if (data.success) {
        this.setState({ activityLogs: data.data || [], activityLogsLoading: false });
      } else {
        this.setState({ activityLogsLoading: false });
        notification.error({ message: "Failed to load activity logs" });
      }
    } catch (error) {
      this.setState({ activityLogsLoading: false });
      notification.error({ message: error.message });
    }
  };

  closeActivityLogsModal = () => {
    this.setState({ activityLogsModalVisible: false, activityLogsRecord: null, activityLogs: [], expandedLogIds: [], activityFilterWallet: "all", activityFilterAction: "all" });
  };

  toggleLogExpand = (logId) => {
    this.setState((prevState) => {
      const { expandedLogIds } = prevState;
      return { expandedLogIds: expandedLogIds.includes(logId) ? expandedLogIds.filter(id => id !== logId) : [...expandedLogIds, logId] };
    });
  };

  // ============ Wallet Detail Modal (from Activity Log) ============
  openWalletDetailFromLog = (walletId) => {
    const { wallets, activityLogs } = this.state;
    let wallet = null;
    if (wallets) {
      Object.values(wallets).forEach(countryWallets => {
        if (Array.isArray(countryWallets)) {
          const found = countryWallets.find(w => w.id === walletId);
          if (found) wallet = found;
        }
      });
    }
    if (!wallet) {
      const logsForWallet = activityLogs.filter(l => l.wallet_id === walletId && (l.old_value || l.new_value)).sort((a, b) => new Date(b.created_date.Time) - new Date(a.created_date.Time));
      if (logsForWallet.length > 0) {
        const latestLog = logsForWallet[0];
        try {
          const jsonStr = latestLog.new_value || latestLog.old_value;
          const data = JSON.parse(jsonStr);
          wallet = { id: walletId, package_name: data.package_name, type: data.type, country: data.country, credit_available: data.credit || data.credit_available, credit_purchased: data.credit || data.credit_available, unit_price: data.unit_price, expire_date: data.expire_date ? { Time: data.expire_date + "T00:00:00Z", Valid: true } : null, status: data.status || "active", isFromLog: true };
        } catch (e) { wallet = { id: walletId, isFromLog: true }; }
      } else { wallet = { id: walletId, isFromLog: true }; }
    }
    this.setState({ walletDetailModalVisible: true, walletDetailData: wallet });
  };

  closeWalletDetailModal = () => {
    this.setState({ walletDetailModalVisible: false, walletDetailData: null });
  };

  // ============ Action Buttons ============
  renderActionButtons = (record) => {
    const canTransfer = record.credit_available === record.credit_purchased;
    const isTrial = record.package_plan === "trial";
    const canDoTransfer = canTransfer && !isTrial;
    const getTransferTooltip = () => { if (isTrial) return "Trial packages cannot be transferred"; if (!canTransfer) return "Transfer ได้เฉพาะ credit ที่เต็มเท่านั้น"; return "Transfer Package"; };
    const getTransferToBalanceTooltip = () => { if (isTrial) return "Trial packages cannot be transferred to balance"; if (!canTransfer) return "Transfer ได้เฉพาะ credit ที่เต็มเท่านั้น"; return "Transfer to Balance"; };
    return (
      <div className="wallet-actions">
        <Tooltip title="Edit Credit"><button type="button" className="wallet-action-modern wallet-action-edit" onClick={() => this.openEditModal(record)}><Icon type="edit" /></button></Tooltip>
        <Tooltip title={getTransferTooltip()}><button type="button" className={`wallet-action-modern wallet-action-transfer ${!canDoTransfer ? 'disabled' : ''}`} disabled={!canDoTransfer} onClick={() => canDoTransfer && this.openTransferModal(record)}><Icon type="swap" /></button></Tooltip>
        <Tooltip title={getTransferToBalanceTooltip()}><button type="button" className={`wallet-action-modern wallet-action-to-balance ${!canDoTransfer ? 'disabled' : ''}`} disabled={!canDoTransfer} onClick={() => canDoTransfer && this.openTransferToBalanceModal(record)}><Icon type="dollar" /></button></Tooltip>
        <Tooltip title="Extend Expiry"><button type="button" className="wallet-action-modern wallet-action-extend" onClick={() => this.openExtendModal(record)}><Icon type="calendar" /></button></Tooltip>
        <Tooltip title="Delete"><button type="button" className="wallet-action-modern wallet-action-delete" onClick={() => this.openDeleteModal(record)}><Icon type="delete" /></button></Tooltip>
      </div>
    );
  };

  render() {
    const { visible, onCancel } = this.props;
    const { tableData, selectedType, selectedCountry, groupedData, member, loading,
      editModalVisible, editRecord, editMode, editCredit, editAdjustment, editNote,
      extendModalVisible, extendRecord, extendDate, extendNote,
      deleteModalVisible, deleteRecord, deleteNote,
      addModalVisible, packages, packagesLoading, selectedPackageId, addNote,
      transferModalVisible, transferRecord, transferPackageId, currentPackageInfo, transferNote,
      transferToBalanceModalVisible, transferToBalanceRecord, transferToBalanceNote, transferToBalanceAmount,
      activityLogsModalVisible, activityLogs, activityLogsLoading, expandedLogIds, activityFilterWallet, activityFilterAction,
      walletDetailModalVisible, walletDetailData,
    } = this.state;

    const columns = [
      { title: "Package", dataIndex: "package_name", key: "package", width: 120, render: (name) => <span className="wallet-package-name">{name || "Standard"}</span> },
      { title: "Unit Price", dataIndex: "unit_price", key: "unit_price", width: 100, align: "right", render: (price) => <span className="wallet-price">${(price || 0).toFixed(4)}</span> },
      { title: "Available", dataIndex: "credit_available", key: "credit_available", width: 120, align: "right", render: (val, record) => { const isSms = record.type === 'sms'; const dollarValue = (record.unit_price || 0) * (val || 0); const content = <span className="wallet-credit-available">{getCurrencyDisplay(val, record.type)}</span>; return isSms ? <Tooltip title={`≈ $${dollarValue.toFixed(4)} USD`}>{content}</Tooltip> : content; } },
      { title: "Purchased", dataIndex: "credit_purchased", key: "credit_purchased", width: 120, align: "right", render: (val, record) => { const isSms = record.type === 'sms'; const dollarValue = (record.unit_price || 0) * (val || 0); const content = <span className="wallet-credit-purchased">{getCurrencyDisplay(val, record.type)}</span>; return isSms ? <Tooltip title={`≈ $${dollarValue.toFixed(4)} USD`}>{content}</Tooltip> : content; } },
      { title: "Expires", dataIndex: "expire_date", key: "expire", width: 110, render: (val) => { const dateStr = formatDate(val); if (val && val.Valid) { const expireDate = new Date(val.Time); const now = new Date(); const daysLeft = Math.ceil((expireDate - now) / (1000 * 60 * 60 * 24)); const isExpiringSoon = daysLeft <= 30 && daysLeft > 0; const isExpired = daysLeft <= 0; return (<span className={isExpired ? "wallet-date-expired" : isExpiringSoon ? "wallet-date-warning" : "wallet-date"}>{dateStr}{isExpiringSoon && !isExpired && <Tag style={{ marginLeft: 4, fontSize: 10, background: "#fff7e6", borderColor: "#ffd591", color: "#d46b08" }}>{daysLeft}d</Tag>}</span>); } return <span className="wallet-date">{dateStr}</span>; } },
      { title: "Status", dataIndex: "status", key: "status", width: 90, render: (status) => this.getStatusTag(status) },
      { title: "Actions", key: "actions", width: 140, align: "center", render: (_, record) => this.renderActionButtons(record) },
    ];

    return (
      <React.Fragment>
        <Modal
          title={<div className="wallet-modal-header"><span className="wallet-modal-title">Credit Wallet</span><span className="wallet-modal-member">{member.memberName}</span><Button type="link" icon="history" onClick={this.openActivityLogsModal} className="wallet-header-btn">View All Activity</Button><Button type="link" icon="close" onClick={() => onCancel()} className="wallet-header-btn wallet-header-close-btn" /></div>}
          visible={visible}
          onCancel={() => onCancel()}
          width={1200}
          bodyStyle={{ padding: 0, height: "600px" }}
          footer={null}
          className="wallet-modal"
          closable={false}
          maskClosable={false}
        >
          <div className="wallet-container">
            <div className="wallet-sidebar">
              <WalletSidebar
                groupedData={groupedData}
                selectedType={selectedType}
                selectedCountry={selectedCountry}
                memberBalance={member.balance}
                onMenuSelect={this.handleMenuSelect}
                onAddClick={this.openAddModal}
                getTotalCredits={this.getTotalCredits}
                getCountryCredits={this.getCountryCredits}
              />
            </div>
            <div className="wallet-content">
              {selectedType ? (
                <React.Fragment>
                  <WalletSummaryCards tableData={tableData} selectedType={selectedType} selectedCountry={selectedCountry} />
                  <Divider style={{ margin: "16px 0" }} />
                  <div className="wallet-table-container">
                    <Table columns={columns} dataSource={tableData} rowKey={(record) => record.id || Math.random()} pagination={tableData.length > 5 ? { pageSize: 5, size: "small" } : false} size="middle" className="wallet-table" />
                  </div>
                </React.Fragment>
              ) : (
                <div className="wallet-empty-state"><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select a wallet type from the sidebar" /></div>
              )}
            </div>
          </div>
        </Modal>

        {/* Modals using imported components */}
        <EditCreditModal visible={editModalVisible} record={editRecord} mode={editMode} credit={editCredit} adjustment={editAdjustment} note={editNote} loading={loading} onModeChange={(mode) => this.setState({ editMode: mode })} onCreditChange={(val) => this.setState({ editCredit: val })} onAdjustmentChange={(val) => this.setState({ editAdjustment: val })} onNoteChange={(val) => this.setState({ editNote: val })} onCancel={this.closeEditModal} onSubmit={this.handleEditSubmit} />
        <ExtendExpiryModal visible={extendModalVisible} record={extendRecord} date={extendDate} note={extendNote} loading={loading} onDateChange={(date) => this.setState({ extendDate: date })} onNoteChange={(val) => this.setState({ extendNote: val })} onCancel={this.closeExtendModal} onSubmit={this.handleExtendSubmit} />
        <DeleteWalletModal visible={deleteModalVisible} record={deleteRecord} note={deleteNote} loading={loading} onNoteChange={(val) => this.setState({ deleteNote: val })} onCancel={this.closeDeleteModal} onSubmit={this.handleDeleteSubmit} />
        <AddCreditModal visible={addModalVisible} packages={packages} packagesLoading={packagesLoading} selectedPackageId={selectedPackageId} note={addNote} loading={loading} onPackageChange={(id) => this.setState({ selectedPackageId: id })} onNoteChange={(val) => this.setState({ addNote: val })} onCancel={this.closeAddModal} onSubmit={this.handleAddSubmit} />
        <TransferPackageModal visible={transferModalVisible} record={transferRecord} packages={packages} packagesLoading={packagesLoading} selectedPackageId={transferPackageId} currentPackageInfo={currentPackageInfo} note={transferNote} loading={loading} onPackageChange={(id) => this.setState({ transferPackageId: id })} onNoteChange={(val) => this.setState({ transferNote: val })} onCancel={this.closeTransferModal} onSubmit={this.handleTransferSubmit} />
        <TransferToBalanceModal visible={transferToBalanceModalVisible} record={transferToBalanceRecord} balanceAmount={transferToBalanceAmount} memberBalance={member.balance} note={transferToBalanceNote} loading={loading} onNoteChange={(val) => this.setState({ transferToBalanceNote: val })} onCancel={this.closeTransferToBalanceModal} onSubmit={this.handleTransferToBalanceSubmit} />
        <ActivityLogsModal visible={activityLogsModalVisible} logs={activityLogs} loading={activityLogsLoading} expandedLogIds={expandedLogIds} filterWallet={activityFilterWallet} filterAction={activityFilterAction} onFilterWalletChange={(val) => this.setState({ activityFilterWallet: val })} onFilterActionChange={(val) => this.setState({ activityFilterAction: val })} onToggleExpand={this.toggleLogExpand} onWalletClick={this.openWalletDetailFromLog} onCancel={this.closeActivityLogsModal} />
        <WalletDetailModal visible={walletDetailModalVisible} walletData={walletDetailData} onCancel={this.closeWalletDetailModal} />
      </React.Fragment>
    );
  }
}

export default MemberCreditWalletModel;



// WEBPACK FOOTER //
// ./src/containers/User/UserManagement/MemberCreditWalletModel.js